import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { View, Text, TextInput, Button, StyleSheet, useWindowDimensions } from 'react-native';
import { GET_PRE_RELEASE_ASSET, UPDATE_SALE_ASSET_PRE_RELEASE } from './queries';
import DataState from '../DataState';

const PreRelease = () => {
  const { hex } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    titleOwnerName: '',
    titleShippingAddress1: '',
    titleShippingAddress2: '',
    titleCity: '',
    titleState: '',
    titleZip: '',
    titleShipOwnerName: '',
    titleShipShippingAddress1: '',
    titleShipShippingAddress2: '',
    titleShipCity: '',
    titleShipState: '',
    titleShipZip: ''
  });

  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const { loading, data } = useQuery(GET_PRE_RELEASE_ASSET, {
    variables: { hex },
  });

  const [updatePreRelease, { loading: saving }] = useMutation(UPDATE_SALE_ASSET_PRE_RELEASE);

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const copyTitleToShip = () => {
    setFormData({
      ...formData,
      titleShipOwnerName: formData.titleOwnerName,
      titleShipShippingAddress1: formData.titleShippingAddress1,
      titleShipShippingAddress2: formData.titleShippingAddress2,
      titleShipCity: formData.titleCity,
      titleShipState: formData.titleState,
      titleShipZip: formData.titleZip,
    });
  };

  const handleSubmit = async () => {
    try {
      await updatePreRelease({
        variables: {
          hex,
          ...formData
        }
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <DataState.Loading />;
  if (!data || !data.preReleaseAsset) return <Text>Invalid Link</Text>;

  if (submitted) {
    return (
      <View style={styles.wrapper}>
        <Text style={styles.header}>Thank you for sending us your release information.</Text>
        <Text style={styles.subheader}>We will send you your release shortly!</Text>
        <Button title="Return Home" onPress={() => window.location = '/account/profile'} />
      </View>
    );
  }

  return (
    <View style={styles.wrapper}>
      <Text style={styles.header}>Provide Title Release Information</Text>
      <Text style={styles.subheader}>
        For: {data.preReleaseAsset.name} (VIN {data.preReleaseAsset.vin})
      </Text>
      <Text>Lot #: {data.preReleaseAsset.soldSaleAsset?.correctedLotNumber}</Text>

      <View style={[styles.columnsContainer, isMobile && styles.stackColumn]}>
        {/* Column 1: Title Info */}
        <View style={styles.sectionBox}>
          <Text style={styles.sectionTitle}>Information on Title / Información en el Título</Text>

          <Text>Name on title / Nombre en el título</Text>
          <TextInput
            value={formData.titleOwnerName}
            onChangeText={text => handleChange('titleOwnerName', text)}
            style={styles.input}
          />

          <Text>Address on title / Dirección en el título</Text>
          <TextInput
            value={formData.titleShippingAddress1}
            onChangeText={text => handleChange('titleShippingAddress1', text)}
            style={styles.input}
          />
          <TextInput
            value={formData.titleShippingAddress2}
            onChangeText={text => handleChange('titleShippingAddress2', text)}
            placeholder="(optional)"
            style={styles.input}
          />

          <Text>City / Ciudad</Text>
          <TextInput
            value={formData.titleCity}
            onChangeText={text => handleChange('titleCity', text)}
            style={styles.input}
          />

          <Text>State / Estado</Text>
          <TextInput
            value={formData.titleState}
            onChangeText={text => handleChange('titleState', text)}
            style={styles.input}
          />

          <Text>Zip / Código Postal</Text>
          <TextInput
            value={formData.titleZip}
            onChangeText={text => handleChange('titleZip', text)}
            style={styles.input}
          />

          <Button title="Copy to Shipping Info / Copiar a Información de Envío" onPress={copyTitleToShip} />
        </View>

        {/* Column 2: Shipping Info */}
        <View style={styles.sectionBox}>
          <Text style={styles.sectionTitle}>Where to Ship Title / Dónde Enviar el Título</Text>

          <Text>Name to ship title to / Nombre a quien se enviará el título</Text>
          <TextInput
            value={formData.titleShipOwnerName}
            onChangeText={text => handleChange('titleShipOwnerName', text)}
            style={styles.input}
          />

          <Text>Address to ship title / Dirección a quien se enviará el título</Text>
          <Text style={styles.note}>
            MUST BE A UNITED STATES ADDRESS and CAN NOT BE A PO BOX / DEBE SER UNA DIRECCIÓN EN ESTADOS UNIDOS Y NO PUEDE SER UN APARTADO POSTAL
          </Text>
          <TextInput
            value={formData.titleShipShippingAddress1}
            onChangeText={text => handleChange('titleShipShippingAddress1', text)}
            style={styles.input}
          />
          <TextInput
            value={formData.titleShipShippingAddress2}
            onChangeText={text => handleChange('titleShipShippingAddress2', text)}
            placeholder="(optional)"
            style={styles.input}
          />

          <Text>City / Ciudad</Text>
          <TextInput
            value={formData.titleShipCity}
            onChangeText={text => handleChange('titleShipCity', text)}
            style={styles.input}
          />

          <Text>State / Estado</Text>
          <TextInput
            value={formData.titleShipState}
            onChangeText={text => handleChange('titleShipState', text)}
            style={styles.input}
          />

          <Text>Zip / Código Postal</Text>
          <TextInput
            value={formData.titleShipZip}
            onChangeText={text => handleChange('titleShipZip', text)}
            style={styles.input}
          />
        </View>
      </View>

      <View style={{ marginTop: 20 }}>
        <Button title={saving ? 'Saving...' : 'Submit / Enviar'} onPress={handleSubmit} disabled={saving} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: { padding: 20 },
  header: { fontSize: 22, fontWeight: 'bold', marginBottom: 10 },
  subheader: { marginBottom: 20 },
  columnsContainer: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 20,
  },
  stackColumn: {
    flexDirection: 'column',
  },
  sectionBox: {
    flex: 1,
    padding: 15,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    marginBottom: 20,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 10,
  },
  input: {
    borderWidth: 1,
    padding: 8,
    marginBottom: 10,
    borderColor: '#999',
  },
  note: {
    fontStyle: 'italic',
    fontSize: 12,
    marginBottom: 5,
  }
});

export default PreRelease;
